<script lang="ts" setup>
import type { InputDateProps } from '@nuxt/ui';
import type { Temporal } from 'temporal-polyfill';
import { tv } from 'tailwind-variants';
import { computed } from 'vue';
import nuxtUiInputTheme from '#build/ui/input';
import DButton from '#layers/ui/components/Button.vue';
import DTooltip from '#layers/ui/components/Tooltip.vue';
import { useMergedTheme } from '#layers/ui/composables/use-merged-theme';
import { useReadonly } from '#layers/ui/composables/use-readonly';
import { plainDateRangeAdapter } from '#layers/ui/utils/range-value-adapter';
import DateTimePickerRangeBase from './RangeBase.vue';

interface Props extends Pick<InputDateProps, 'color' | 'variant' | 'size' | 'disabled'> {
	readonly?: boolean;
	block?: boolean;
	disableFutureDates?: boolean;
}

const {
	block = true,
	disableFutureDates = false,
	color,
	variant,
	size,
	readonly,
	disabled,
} = defineProps<Props>();

const startDate = defineModel<Temporal.PlainDate | null>('startDate');
const endDate = defineModel<Temporal.PlainDate | null>('endDate');

const pickerProps = computed(() => ({ size, disabled, readonly, block, disableFutureDates }));

const inputTheme = useMergedTheme({ input: nuxtUiInputTheme });
const theme = computed(() => tv(inputTheme)({
	color,
	variant,
	size,
	leading: true,
	trailing: true,
}));

const { preventKeyboardNavigation, preventPointer } = useReadonly(() => readonly);
</script>

<template>
	<DateTimePickerRangeBase
		v-bind="pickerProps"
		v-model:start-date="startDate"
		v-model:end-date="endDate"
		:adapter="plainDateRangeAdapter"
	>
		<template #trigger="{ rangeFieldDisplay, isRangeEmpty, canClearRange, clearRange }">
			<button
				type="button"
				data-testid="date-range-trigger"
				:disabled
				:aria-readonly="readonly"
				:class="theme.root({
					class: [
						block ? 'w-full' : '',
						disabled ? 'opacity-50' : '',
						disabled ? 'cursor-not-allowed' : readonly ? 'cursor-default' : 'cursor-pointer',
					],
				})"
				@pointerdown.capture="preventPointer"
				@click.capture="preventPointer"
				@keydown.capture="preventKeyboardNavigation"
			>
				<span :class="theme.leading()">
					<DIcon name="lucide:calendar" :class="theme.leadingIcon()" />
				</span>

				<span
					:class="theme.base({
						class: [
							'w-full truncate text-start select-none',
							isRangeEmpty ? 'text-dimmed' : 'text-default',
							disabled ? 'cursor-not-allowed' : readonly ? 'cursor-default' : 'cursor-pointer',
						],
					})"
				>
					{{ rangeFieldDisplay }}
				</span>

				<span :class="theme.trailing()">
					<DTooltip v-if="canClearRange" :text="$t('common.clear')">
						<DButton
							type="button"
							color="neutral"
							variant="link"
							icon="lucide:x"
							size="sm"
							data-testid="date-range-clear-range"
							class="p-0 text-dimmed"
							:disabled
							@click.stop="clearRange"
							@pointerdown.stop
						/>
					</DTooltip>
					<DIcon v-else-if="!readonly" name="lucide:chevron-down" :class="theme.trailingIcon()" />
				</span>
			</button>
		</template>
	</DateTimePickerRangeBase>
</template>
